import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Plus, DollarSign, Users, Save, X, Sparkles, Trash2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

/**
 * MasterOrgs — Master-only.
 *  - Table of all orgs. Click one → drawer with per-role pricing editor and
 *    bundle-seat pre-purchase form.
 *  - "New organization" wizard creates an org + primary Controller in one shot
 *    (used for demos + pre-sales onboarding).
 */
export default function MasterOrgs() {
  const nav = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null); // org detail
  const [createOpen, setCreateOpen] = useState(false);

  const loadList = () => {
    api.get("/master/orgs").then((r) => setOrgs(r.data || [])).catch(() => setOrgs([]));
  };
  useEffect(() => { loadList(); }, []);

  const openOrg = async (id) => {
    try { const r = await api.get(`/master/orgs/${id}`); setSelected(r.data); }
    catch { toast.error("Failed to load org"); }
  };

  return (
    <div className="p-4 lg:p-8 space-y-5" data-testid="master-orgs-page">
      <div className="flex items-center gap-2">
        <Link to="/app/master" className="btn-back" data-testid="mo-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Master
        </Link>
        <div className="flex-1" />
        <button onClick={() => setCreateOpen(true)} data-testid="mo-new-org"
          className="btn-pill btn-pill-primary">
          <Plus className="w-3.5 h-3.5" /> New organization
        </button>
      </div>

      <section className="hero-navy rounded-3xl p-6 text-white">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/70">Master Controller · Organizations</div>
        <h1 className="mt-1 font-display font-black text-3xl tracking-tight">Tenant control room.</h1>
        <p className="mt-2 text-sm text-white/75 max-w-xl">
          Click an org to override its per-seat pricing or pre-purchase bundled seats. &ldquo;New organization&rdquo; seeds a demo org + Controller login you can hand off to a prospect.
        </p>
      </section>

      <div className="surface-card rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#1a1a1a] text-white">
            <tr>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Org</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold">Owner</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold hidden md:table-cell">Created</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold hidden md:table-cell">Pricing</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]/06">
            {orgs.map((o) => (
              <tr key={o.id} data-testid={`mo-row-${o.id}`} className="hover:bg-[#f7f5f2]">
                <td className="px-4 py-2.5 font-display font-bold text-[13px]">
                  {o.name}
                  {o.is_demo && <span className="ml-2 text-[9px] uppercase tracking-wider text-[#a13ad6] font-bold">DEMO</span>}
                </td>
                <td className="px-4 py-2.5 text-[12px]">{o.owner_email || "—"}</td>
                <td className="px-4 py-2.5 text-[10px] font-mono text-[#1a1a1a]/60 hidden md:table-cell">{o.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-2.5 text-[10px] font-mono hidden md:table-cell">
                  {o.pricing_locked ? <span className="text-[#ff4500] font-bold">Custom</span> : <span className="text-[#1a1a1a]/50">Default</span>}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => openOrg(o.id)} data-testid={`mo-open-${o.id}`}
                    className="text-[10px] uppercase tracking-wider font-bold text-[#ff4500] hover:underline">
                    Open →
                  </button>
                </td>
              </tr>
            ))}
            {orgs.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
                No orgs yet — create one to begin.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <OrgDetailDialog org={selected} onClose={() => setSelected(null)}
          onSaved={() => { openOrg(selected.id); loadList(); }} />
      )}
      {createOpen && (
        <CreateOrgDialog onClose={() => setCreateOpen(false)}
          onCreated={(res) => { setCreateOpen(false); loadList(); toast.success(`Created ${res.controller_email}`); }} />
      )}
    </div>
  );
}

function OrgDetailDialog({ org, onClose, onSaved }) {
  const [pricing, setPricing] = useState(() => JSON.parse(JSON.stringify(org.pricing || {})));
  const [bundle, setBundle] = useState({ seat_type: "user", quantity: 10, price_setup: 0, price_monthly: 8, note: "" });
  const [busy, setBusy] = useState(false);

  const savePricing = async () => {
    setBusy(true);
    try {
      await api.patch(`/master/orgs/${org.id}/pricing`, { pricing });
      toast.success("Pricing overridden for this org");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const addBundle = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/master/orgs/${org.id}/bundle-seats`, bundle);
      toast.success(`Created ${r.data.created} bundled seat(s)`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const removeSeat = async (sid) => {
    if (!confirm("Delete this empty bundle seat?")) return;
    try {
      await api.delete(`/master/orgs/${org.id}/bundle-seats/${sid}`);
      toast.success("Removed");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#ff4500]" /> {org.name}
          </DialogTitle>
          <DialogDescription className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
            {org.member_count} members · {(org.bundle_seats || []).length} bundle seats
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-auto py-2">
          {/* Pricing override */}
          <section>
            <div className="flex items-center gap-1 text-[#ff4500] mb-1"><DollarSign className="w-3.5 h-3.5" /><div className="font-mono text-[10px] uppercase tracking-[0.2em]">// Per-seat pricing override</div></div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-wider text-[#1a1a1a]/55">
                  <th>Role</th><th>Setup ($)</th><th>Monthly ($)</th>
                </tr>
              </thead>
              <tbody>
                {["controller", "admin", "user"].map((role) => (
                  <tr key={role} className="border-t border-[#1a1a1a]/06">
                    <td className="py-1.5 font-display font-bold uppercase text-[12px]">{role}</td>
                    <td>
                      <input type="number" min="0" value={pricing[role]?.setup ?? 0}
                        data-testid={`mo-setup-${role}`}
                        onChange={(e) => setPricing({ ...pricing, [role]: { ...pricing[role], setup: Number(e.target.value) } })}
                        className="w-24 border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm" />
                    </td>
                    <td>
                      <input type="number" min="0" value={pricing[role]?.monthly ?? 0}
                        data-testid={`mo-monthly-${role}`}
                        onChange={(e) => setPricing({ ...pricing, [role]: { ...pricing[role], monthly: Number(e.target.value) } })}
                        className="w-24 border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={savePricing} disabled={busy} data-testid="mo-save-pricing"
              className="mt-2 btn-pill btn-pill-primary text-xs disabled:opacity-50">
              <Save className="w-3 h-3" /> Save pricing
            </button>
          </section>

          {/* Bundle-seats */}
          <section>
            <div className="flex items-center gap-1 text-[#a13ad6] mb-1"><Users className="w-3.5 h-3.5" /><div className="font-mono text-[10px] uppercase tracking-[0.2em]">// Pre-purchase seat bundle</div></div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <select value={bundle.seat_type} onChange={(e) => setBundle({ ...bundle, seat_type: e.target.value })}
                data-testid="mo-bundle-type"
                className="border border-[#1a1a1a]/20 rounded-md px-2 py-1.5 text-sm bg-white">
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="controller">Controller</option>
              </select>
              <input type="number" min="1" placeholder="Qty" value={bundle.quantity}
                data-testid="mo-bundle-qty"
                onChange={(e) => setBundle({ ...bundle, quantity: Number(e.target.value) })}
                className="border border-[#1a1a1a]/20 rounded-md px-2 py-1.5 text-sm" />
              <input type="number" min="0" placeholder="Setup $" value={bundle.price_setup}
                data-testid="mo-bundle-setup"
                onChange={(e) => setBundle({ ...bundle, price_setup: Number(e.target.value) })}
                className="border border-[#1a1a1a]/20 rounded-md px-2 py-1.5 text-sm" />
              <input type="number" min="0" placeholder="Monthly $" value={bundle.price_monthly}
                data-testid="mo-bundle-monthly"
                onChange={(e) => setBundle({ ...bundle, price_monthly: Number(e.target.value) })}
                className="border border-[#1a1a1a]/20 rounded-md px-2 py-1.5 text-sm" />
              <button onClick={addBundle} disabled={busy}
                data-testid="mo-bundle-add"
                className="btn-pill btn-pill-primary text-xs disabled:opacity-50">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <input placeholder="Note (optional, e.g. '20-seat discounted bundle')" value={bundle.note}
              onChange={(e) => setBundle({ ...bundle, note: e.target.value })}
              className="mt-2 w-full border border-[#1a1a1a]/20 rounded-md px-2 py-1.5 text-sm" />
            {(org.bundle_seats || []).length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 font-bold mb-1">Existing seats</div>
                <div className="space-y-1 max-h-40 overflow-auto">
                  {org.bundle_seats.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 text-[11px] p-1.5 border border-[#1a1a1a]/06 rounded">
                      <span className="font-mono uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#a13ad6]/15 text-[#a13ad6]">{s.seat_type}</span>
                      <span>${s.price_setup} setup / ${s.price_monthly}/mo</span>
                      <span className="text-[#1a1a1a]/50">{s.note}</span>
                      <span className="ml-auto text-[9px] uppercase tracking-wider">{s.filled_by_user_id ? "Filled" : "Empty"}</span>
                      {!s.filled_by_user_id && (
                        <button onClick={() => removeSeat(s.id)} className="p-1 text-red-500"><Trash2 className="w-3 h-3" /></button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-ghost"><X className="w-3.5 h-3.5" /> Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateOrgDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ org_name: "", controller_email: "", controller_name: "", controller_password: "", is_demo: true });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.org_name || !form.controller_email || !form.controller_name || !form.controller_password) {
      toast.error("All fields required"); return;
    }
    setBusy(true);
    try {
      const r = await api.post("/master/orgs/provision", form);
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#ff4500]" /> New organization
          </DialogTitle>
          <DialogDescription className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
            Creates an org + Controller login. Traceability preserved when demo converts to paid.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Row label="Organization name">
            <input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })}
              data-testid="co-org-name"
              className="w-full border border-[#1a1a1a]/20 rounded-md px-3 py-2 text-sm" />
          </Row>
          <Row label="Controller email">
            <input type="email" value={form.controller_email} onChange={(e) => setForm({ ...form, controller_email: e.target.value })}
              data-testid="co-ctrl-email"
              className="w-full border border-[#1a1a1a]/20 rounded-md px-3 py-2 text-sm" />
          </Row>
          <Row label="Controller name">
            <input value={form.controller_name} onChange={(e) => setForm({ ...form, controller_name: e.target.value })}
              data-testid="co-ctrl-name"
              className="w-full border border-[#1a1a1a]/20 rounded-md px-3 py-2 text-sm" />
          </Row>
          <Row label="Initial password (share via secure channel)">
            <input value={form.controller_password} onChange={(e) => setForm({ ...form, controller_password: e.target.value })}
              data-testid="co-ctrl-password"
              className="w-full border border-[#1a1a1a]/20 rounded-md px-3 py-2 text-sm" />
          </Row>
          <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider cursor-pointer">
            <input type="checkbox" checked={form.is_demo}
              data-testid="co-is-demo"
              onChange={(e) => setForm({ ...form, is_demo: e.target.checked })} className="accent-[#ff4500]" />
            Mark as demo (visible flag in admin console)
          </label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-ghost"><X className="w-3.5 h-3.5" /> Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="co-submit"
            className="btn-pill btn-pill-primary disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
